import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import useRequest from '@common/react/hooks/useRequest';
import { phoneRegexp } from '@common/react/utils/validationHelpers';

import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import { CustomPhoneControl } from '@app/components/Forms/CustomPhoneControl/CustomPhoneControl';
import CustomSelect from '@app/components/Forms/CustomSelect/CustomSelect';
import { stringBeautifyValidator } from '@app/components/LocalCommon/Utils/Validators';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomModalMessage from '@app/components/UI/CustomModalMessage/CustomModalMessage';
import CloseSVG from '@app/images/icons/CloseSVG';
import { InquiryReason } from '@app/objects/InquiryReason';
import '@app/scss/components/contactUs.scss';

const userIcon = require('@app/images/icons/user.svg');
const phoneIcon = require('@app/images/icons/phone.svg');
const mailIcon = require('@app/images/icons/mail.svg');

interface ContactUsProps {
	onChangeVisibleModal: (e: boolean) => void;
}

interface FormValues {
	name: string;
	lastName: string;
	email: string;
	phone: string;
	reason: InquiryReason | null;
	text: string;
	captcha: string;
}

const options = [
	{ id: InquiryReason.Question + 1, label: 'Ask a question' },
	{ id: InquiryReason.Assistance + 1, label: 'Need Assistance' },
	{ id: InquiryReason.Problem + 1, label: 'Report a problem' },
	{ id: InquiryReason.Suggestion + 1, label: 'Make a suggestion' },
];

const initialValues: FormValues = {
	name: '',
	lastName: '',
	email: '',
	phone: '',
	reason: null,
	text: '',
	captcha: '',
};

const validationSchema = Yup.object({
	name: stringBeautifyValidator('First Name is a required field'),
	lastName: stringBeautifyValidator('Last Name is a required field'),
	email: Yup.string().email('Value must be a valid Email address').required('Email is a required field'),
	phone: Yup.string().matches(phoneRegexp, 'Enter 10-digit phone number').required('Phone is a required field'),
	reason: Yup.number().nullable('Reason is a required field').required('Reason is a required field'),
	text: stringBeautifyValidator('Message is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const ContactUs: React.FC<ContactUsProps> = ({ onChangeVisibleModal }) => {
	const request = useRequest();
	const [sending, setSending] = React.useState<boolean>(false);
	const [submitted, setSubmitted] = React.useState<boolean>(false);
	const [messageModal, setMessageModal] = React.useState<boolean>(false);
	const [message, setMessage] = React.useState<string>('');
	const [childrenModal, setChildrenModal] = React.useState<React.ReactNode>();

	const closeModal = () => {
		onChangeVisibleModal(false);
	};

	const closeModalMessage = () => {
		if (submitted) {
			setMessageModal(false);
			closeModal();
		} else {
			setMessageModal(false);
		}
	};

	const handleSubmit = (values: FormValues) => {
		if (values.reason !== null) {
			values.reason -= 1;

			setSending(true);
			setChildrenModal('');

			request('Inquiry', values)
				.then(() => {
					setMessage('Your inquiry has been sent.');
					setChildrenModal(<p>
						Thank you for contacting the Pet Hospice Journal.
						<br />
						You'll be hearing from us soon.
					</p>);
					setSubmitted(true);
					setMessageModal(true);
				})
				.catch((e) => {
					setMessage(e.message);
					setMessageModal(true);
				})
				.finally(() => {
					setSending(false);
				});
		}
	};

	return (
		<div className="contact-us is-relative">
			<CloseSVG className="close" onClick={closeModal} />
			<h1 className="title h2 text-center">Contact Us</h1>
			<Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
				{(formikBag: FormikProps<FormValues>) => {
					return (
						<Form className="create-profile__form" autoComplete="new-password">
							<div className="create-profile__form-fields">
								<CustomInput name="name" placeholder="First Name*" formikBag={formikBag} prefixIcon={userIcon} />
								<CustomInput name="lastName" placeholder="Last Name*" formikBag={formikBag} prefixIcon={userIcon} />
								<CustomInput name="email" type="email" placeholder="Email*" formikBag={formikBag} prefixIcon={mailIcon} />
								<FormikField
									title="Phone*"
									fieldName="phone"
									type="tel"
									containerClassName="custom-input"
									render={(fieldProps) => (
										<CustomPhoneControl
											withReplace={false}
											placeholder="Phone*"
											fieldProps={fieldProps}
											prefixIcon={phoneIcon}
										/>
									)}
								/>
								<CustomSelect
									name="reason"
									placeholder="Reason*"
									onSelect={(value, option) => {
										formikBag.setFieldValue('reason', value);
									}}
									onChange={(value, option) => {
										formikBag.setFieldValue('reason', value);
									}}
									options={options}
									showSearch={false}
									onFocus={() => {}}
									transform
									formikBag={formikBag}
								/>
								<div className="textarea">
									<CustomInput
										component="textarea"
										name="text"
										placeholder="Message*"
										formikBag={formikBag}
									/>
								</div>
								<div className="create-profile__form-captcha">
									<FormikField
										containerClassName="captcha"
										fieldName="captcha"
										render={(fieldProps) => (
											<ReCAPTCHA
												sitekey="6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU"
												onChange={(value) => {
													if (value) {
														formikBag.setFieldValue('captcha', value || '');
													} else {
														formikBag.setFieldValue('captcha', '');
													}
												}}
												onExpired={() => {
													formikBag.setFieldValue('captcha', 'expired');
												}}
												hl="en"
											/>
										)}
									/>
								</div>
							</div>
							<div className="btns">
								{!sending ? (
									<>
										<button type="submit" className="btn-revert btn-primary btn-md">
											Send
										</button>
										<button type="button" className="btn-revert btn-light-purple btn-md" onClick={closeModal}>
											Cancel
										</button>
									</>
								) : (
									<BounceLoader />
								)}
							</div>
						</Form>
					);
				}}
			</Formik>
			{messageModal && (
				<CustomModalMessage
					modalVisible={messageModal}
					setModalVisible={closeModalMessage}
					title={message}
					buttonClass="btn-light-purple btn-md"
					addClass="contact-form__modal"
				>
					{childrenModal}
				</CustomModalMessage>
			)}
		</div>
	);
};

export default ContactUs;
